import React, { useEffect } from 'react'
import { Box } from '@hub/box'
import { Text } from '@hub/text'
import { Stack } from '@hub/stack'
import {
  ActionSheet,
  ActionSheetContent,
  ActionSheetTrigger,
  ActionSheetCTA,
} from '@scentregroup/shared/hub-components/action-sheet'
import {
  GridViewMediumIcon,
  ListViewMediumIcon,
  SortAscendingMediumIcon,
} from '@hub/icon'
import { Radio, RadioGroup } from '@hub/radio'
import { ViewType } from '@scentregroup/shared/hub-components/view-control'
import { FilterFormData } from './types'
import { Grid } from '@hub/grid'

export type ActionSheetContainerProps = {
  data: FilterFormData
  onChange: (data: FilterFormData) => void
}

const ActionSheetContainer: React.FC<ActionSheetContainerProps> = ({
  data,
  onChange,
}) => {
  const [sortBy, setSortBy] = React.useState(data.sortBy)
  const [viewMode, setViewMode] = React.useState(data.viewMode)

  const handleFormClick = (): void => {
    // reset pagination if the sort direction is changed
    const pagination = sortBy !== data.sortBy && { page: undefined }
    onChange({
      viewMode,
      sortBy,
      ...pagination,
    })
  }

  useEffect(() => {
    setSortBy(data.sortBy)
    setViewMode(data.viewMode)
  }, [data])

  return (
    <Box width={['100%', null, 300]}>
      <ActionSheet onClick={handleFormClick}>
        <ActionSheetTrigger gap={['spacing-md', null, 'spacing-lg']}>
          <Text
            sx={{
              flexGrow: 1,
              fontWeight: ['bold', null, 'normal'],
            }}
          >
            Sort and filter
          </Text>
          <SortAscendingMediumIcon
            boxSize="size-icon-md"
            sx={{
              flexGrow: 0,
              display: [null, null, 'none'],
            }}
          />
        </ActionSheetTrigger>
        <ActionSheetContent>
          <Stack gap={['spacing-3xl', null, '0']}>
            <Stack gap="spacing-md">
              <Text tone="strong" fontSize="font-md">
                Sort by
              </Text>
              <RadioGroup
                onChange={(nextValue: FilterFormData['sortBy']) =>
                  setSortBy(nextValue)
                }
                value={sortBy}
              >
                <Stack gap="spacing-md">
                  <Radio value="asc">A-Z</Radio>
                  <Radio value="desc">Z-A</Radio>
                </Stack>
              </RadioGroup>
            </Stack>
            <Box
              sx={{
                display: [null, null, 'none'],
              }}
            >
              <Stack gap="spacing-sm">
                <Text tone="strong" fontSize="font-md">
                  View
                </Text>
                <RadioGroup
                  onChange={(nextValue: ViewType) => setViewMode(nextValue)}
                  value={viewMode === 'auto' ? 'grid' : viewMode}
                >
                  <Grid
                    templateColumns={'auto 1fr'}
                    rowGap="spacing-md"
                    columnGap="spacing-md"
                    sx={{ alignItems: 'center' }}
                  >
                    <Radio value="grid">Grid view</Radio>
                    <GridViewMediumIcon boxSize="size-icon-md" />
                    <Radio value="list">List view</Radio>
                    <ListViewMediumIcon boxSize="size-icon-md" />
                  </Grid>
                </RadioGroup>
              </Stack>
            </Box>
          </Stack>
        </ActionSheetContent>
        <ActionSheetCTA>Apply</ActionSheetCTA>
      </ActionSheet>
    </Box>
  )
}

export default ActionSheetContainer
